import { Select } from '@/components/Select/Select';
import type { SelectOption } from '@/components/Select/select.types';
import { YStack } from 'tamagui';
import { FieldTitle } from '@components/FieldTitle/FieldTitle';

interface SelectFieldProps {
  /** Field label (displayed above the field) */
  label?: string;
  /** Available options to choose from */
  options: SelectOption[];
  /** Currently selected value */
  value: string | null | undefined;
  /** Callback when value changes */
  onValueChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /**  Whether the field is required */
  required?: boolean;
}

/**
 * SelectField - A dropdown select field with optional label
 *
 * Features:
 * - Dropdown selection using the existing Select component
 * - Optional label above the field
 * - Required indicator (*)
 * - Placeholder support
 *
 * @example
 * <SelectField
 *   label="Species"
 *   options={speciesOptions}
 *   value={species}
 *   onValueChange={setSpecies}
 *   placeholder="Select species or group"
 *   required
 * />
 */
export const SelectField = ({
  label,
  options,
  value,
  onValueChange,
  placeholder,
  required = false,
}: SelectFieldProps): React.ReactElement => {
  return (
    <YStack space="$2">
      {/* Label */}
      <FieldTitle label={label} required={required} />

      {/* Select dropdown */}
      <Select
        options={options}
        value={value || ''}
        onValueChange={onValueChange}
        placeholder={placeholder}
        snapPoints={options.length > 10 ? [80] : undefined}
      />
    </YStack>
  );
};
